import { useEffect, type ReactElement } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useTransitionNavigation } from "../shared/hooks/useTransitionNavigation";
import { useAuth } from "./AuthContext";

/**
 * ProtectedRoute wrapper that requires authentication.
 * Redirects to /sign-in if user is not authenticated.
 * Preserves the attempted location for post-login redirect.
 */
export function ProtectedRoute(): ReactElement {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { navigateImmediately } = useTransitionNavigation();

  // Use effect to handle navigation with View Transitions when user logs out
  useEffect(() => {
    if (!user && !loading) {
      console.log("🔒 ProtectedRoute: Redirecting to /sign-in from", location.pathname);
      navigateImmediately("/sign-in");
    }
  }, [user, loading, location.pathname, navigateImmediately]);

  // Show minimal loading state while checking auth (prevents flash of wrong content)
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-lumi-accent border-t-transparent rounded-full animate-spin" />
          <div className="text-neutral-400 text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  // Redirect to sign-in if not authenticated (fallback for non-VT browsers)
  if (!user) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // User is authenticated, render child routes
  return <Outlet />;
}

/**
 * RoleProtectedRoute wrapper that requires specific user role.
 * Redirects to /home if user doesn't have required role.
 */
type RoleProtectedRouteProps = {
  readonly allowedRoles: Array<"ADMIN" | "CONTENT_PROVIDER" | "VIEWER">;
};

export function RoleProtectedRoute({ allowedRoles }: RoleProtectedRouteProps): ReactElement {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show minimal loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-lumi-accent border-t-transparent rounded-full animate-spin" />
          <div className="text-neutral-400 text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  // Redirect to sign-in if not authenticated
  if (!user) {
    console.log("🔒 RoleProtectedRoute: Redirecting to /sign-in from", location.pathname);
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // Check if user has required role
  if (!allowedRoles.includes(user.role)) {
    console.warn(
      "🚫 RoleProtectedRoute: User",
      user.email,
      "with role",
      user.role,
      "denied access to",
      location.pathname,
    );
    // Redirect to home with error message
    return <Navigate to="/home" state={{ error: "You don't have permission to access this page" }} replace />;
  }

  // User is authenticated and has required role
  return <Outlet />;
}
